'use client'

import * as SheetPrimitive from '@radix-ui/react-dialog'
import { tv, type VariantProps } from '../utils'

const sheetOverlayStyles = tv({
	base: 'fixed desktop:rounded-[10px] inset-0 z-50 bg-[#f6f6f3]/60 dark:bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
})

const SheetOverlay = ({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Overlay>) => (
	<SheetPrimitive.Overlay className={sheetOverlayStyles({ class: className })} {...props} />
)
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetContentStyles = tv({
	base: 'fixed z-50 gap-4 transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-200 data-[state=open]:duration-300',
	variants: {
		side: {
			top: 'inset-x-0 top-0 data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
			bottom: 'inset-x-0 bottom-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
			left: 'inset-y-0 left-0 h-full w-3/4 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
			right: 'inset-y-0 right-0 h-full w-3/4 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-[520px]',
		},
	},
	defaultVariants: {
		side: 'right',
	},
})

const sheetInnerStyles = tv({
	base: 'border w-full h-full bg-[#FAFAF9] dark:bg-[#0C0C0C] p-6 relative overflow-hidden',
})

type SheetContentProps = React.ComponentProps<typeof SheetPrimitive.Content> &
	VariantProps<typeof sheetContentStyles> & {
		stack?: boolean
	}

const SheetContent = ({ side = 'right', stack = false, className, children, title, ...props }: SheetContentProps) => (
	<SheetPortal>
		<SheetOverlay />
		<SheetPrimitive.Content
			onOpenAutoFocus={e => e.preventDefault()}
			className={sheetContentStyles({ side, class: 'md:p-4' })}
			aria-describedby={props['aria-describedby'] ?? undefined}
			{...props}
		>
			<div className={sheetInnerStyles({ class: className })}>
				<SheetTitle className='sr-only'>{title}</SheetTitle>
				{children}
			</div>
		</SheetPrimitive.Content>
	</SheetPortal>
)
SheetContent.displayName = SheetPrimitive.Content.displayName

const sheetHeaderStyles = tv({
	base: 'flex flex-col space-y-2 text-center sm:text-left',
})

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={sheetHeaderStyles({ class: className })} {...props} />
)
SheetHeader.displayName = 'SheetHeader'

const sheetFooterStyles = tv({
	base: 'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
})

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={sheetFooterStyles({ class: className })} {...props} />
)
SheetFooter.displayName = 'SheetFooter'

const sheetTitleStyles = tv({
	base: 'text-lg font-semibold text-foreground',
})

const SheetTitle = ({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Title>) => (
	<SheetPrimitive.Title className={sheetTitleStyles({ class: className })} {...props} />
)
SheetTitle.displayName = SheetPrimitive.Title.displayName

const sheetDescriptionStyles = tv({
	base: 'text-sm text-muted-foreground',
})

const SheetDescription = ({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Description>) => (
	<SheetPrimitive.Description className={sheetDescriptionStyles({ class: className })} {...props} />
)
SheetDescription.displayName = SheetPrimitive.Description.displayName

const Sheet = SheetPrimitive.Root
const SheetTrigger = SheetPrimitive.Trigger
const SheetClose = SheetPrimitive.Close
const SheetPortal = SheetPrimitive.Portal

export {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetOverlay,
	SheetPortal,
	SheetTitle,
	SheetTrigger,
}
